import { shortNumber } from '@/mixins/formatter';
import {
    BASE_OVERSIZED_BUY_THRESHOLD,
    BUY_SIZE_RULE_METRICS,
    MARKET_DEFAULT_BUY_POSITIONS,
} from '@/constants/trading';

export function buySizeMetricValue(asset, metric) {
    if (!Number.isFinite(asset.price)) return null;
    if (metric === 'price') return asset.price;
    if (!Array.isArray(asset.balances)) return null;

    if (metric === 'holdings_value') {
        return asset.balances.reduce((sum, b) => sum + b.shares, 0) * asset.price;
    }
    if (metric === 'free_shares') {
        // share count, not a dollar value
        const trading = asset.balances.reduce((sum, b) => sum + (b.bucket?.type === 'trading' ? b.shares : 0), 0);
        return trading - (asset.locked_shares || 0);
    }
    return null;
}

// legacy flat rule ({metric, below, positions}) → single-condition bundle
export function normalizeBuySizeRule(rule) {
    if (Array.isArray(rule.conditions)) return rule;
    return { conditions: [{ metric: rule.metric, below: rule.below }], positions: rule.positions };
}

function conditionMatches(asset, condition) {
    const below = Number(condition.below);
    if (!(below > 0)) return false;

    const value = buySizeMetricValue(asset, condition.metric);
    return value !== null && value < below;
}

// all conditions must hold (AND)
export function buySizeRuleMatches(asset, rule) {
    const normalized = normalizeBuySizeRule(rule);
    if (!normalized.conditions.length) return false;
    return normalized.conditions.every(condition => conditionMatches(asset, condition));
}

export function buySizeConditionLabel(condition) {
    const metric = BUY_SIZE_RULE_METRICS[condition.metric] || condition.metric;
    const threshold = (condition.metric === 'free_shares' ? '' : '$') + shortNumber(condition.below);
    return metric + ' < ' + threshold;
}

export function buySizeRuleLabel(rule, positionSize) {
    const normalized = normalizeBuySizeRule(rule);
    const conditions = normalized.conditions.map(buySizeConditionLabel).join(' AND ');
    return `$${shortNumber(normalized.positions * positionSize)}: ${conditions}`;
}

export function resolveBuySize(asset, rules) {
    if (!asset) return { positions: 1, rule: null };

    let matched = null;
    for (const rule of rules || []) {
        if (!(Number(rule.positions) > 0)) continue;
        if (!buySizeRuleMatches(asset, rule)) continue;
        if (!matched || Number(rule.positions) > Number(matched.positions)) matched = rule;
    }
    const marketDefault = MARKET_DEFAULT_BUY_POSITIONS[asset.market] ?? 1;
    const defaultPositions = asset.meta?.defaultNumberPositions || marketDefault;

    // the default competes too: a rule only wins when it's larger
    if (matched && Number(matched.positions) > defaultPositions) {
        return { positions: Number(matched.positions), rule: normalizeBuySizeRule(matched) };
    }
    return { positions: defaultPositions, rule: null };
}

const state = () => ({
    defaultPositionSize: 50,
    positionMap: new Map(),
    lastOpenPositions: [],
    actionablePositions: [],
    // ticker -> untruncated actionable total; actionablePositions is only a sample
    actionableCounts: {},
    requestedSync: null,
    lastSync: null,
})

const getters = {
    oversizedBuyThreshold: (state, getters) => (asset) => {
        const raw = asset && asset.meta ? asset.meta.defaultNumberPositions : null;
        const configured = Number(raw);
        const safe = Number.isFinite(configured) ? configured : 0;
        const resolved = getters.resolveBuySize(asset);
        // a matched rule raises the threshold, never lowers it
        const rulePositions = resolved.rule ? resolved.positions : 0;
        return Math.max(BASE_OVERSIZED_BUY_THRESHOLD, safe, rulePositions);
    },
    resolveBuySize: () => (asset) => {
        return resolveBuySize(asset, asset && asset.meta ? asset.meta.buySizeRules : null);
    },
}

const actions = {
    registerPositionList(context, positionList) {
        for ( let i = 0; i < positionList.length; i++) {
            context.commit('newlyAddedPosition', positionList[i]);
        }
    },
    removePositionsFromActionable(context, pidList) {
        for (var i = 0; i < pidList.length; i++) {
            context.commit("removeActionablePosition", pidList[i]);
        }
    },
    ingestActionablePositions(context, { positions, counts }) {
        context.commit("saveActionablePositions", positions);
        context.commit("saveActionableCounts", counts);
    },
    updateActionableCount(context, payload) {
        context.commit("setActionableCount", payload);
    },
}

const mutations = {
    saveActionablePositions(state, positions) {
        state.actionablePositions = positions;
    },
    saveActionableCounts(state, counts) {
        state.actionableCounts = counts || {};
    },
    setActionableCount(state, { ticker, count }) {
        // whole-object replacement so a ticker seen for the first time stays reactive
        state.actionableCounts = { ...state.actionableCounts, [ticker]: count };
    },
    removeActionablePosition(state, pid) {
        // iterate backwards so splicing doesn't skip a following duplicate match
        for (var i = state.actionablePositions.length - 1; i >= 0; i--) {
            if (state.actionablePositions[i].pid === pid) {
                state.actionablePositions.splice(i, 1);
            }
        }
    },
    newlyAddedPosition(state, position) {
        state.positionMap.set(position.pid, position);
        if (!state.lastOpenPositions.includes(position.pid)) {
            state.lastOpenPositions.push(position.pid);
        }
    },
    updateRequestedSync(state) {
        state.requestedSync = new Date;
    },
}

export default {
    state,
    getters,
    actions,
    mutations,
}